import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { toast, alert as sweetAlert } from '../utils/toast';
import { LoadingState, EmptyState, Breadcrumbs, BreadcrumbItem } from '../components/ui';
import type { Client } from '../types';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  Calendar,
  Check,
  X,
  FileText,
  UserCheck,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

export const PendingRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowRejectModal(false);
        setSelectedClient(null);
        setRejectionReason('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: pendingClients, isLoading } = useQuery({
    queryKey: ['pending-clients'],
    queryFn: async () => {
      const response = await api.get<any>('/admin/clients?status=pending');
      const payload = response.data;
      return (Array.isArray(payload) ? payload : payload?.data || []) as Client[];
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (clientId: string) => {
      await api.put(`/admin/clients/${clientId}/status`, { status: 'approved' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pending-clients'] });
      toast.success('Cliente aprovado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao aprovar cliente.');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (data: { clientId: string; reason: string }) => {
      await api.put(`/admin/clients/${data.clientId}/status`, {
        status: 'rejected',
        reason: data.reason,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pending-clients'] });
      setShowRejectModal(false);
      setSelectedClient(null);
      setRejectionReason('');
      toast.success('Registo do cliente rejeitado.');
    },
    onError: () => {
      toast.error('Erro ao rejeitar cliente.');
    },
  });

  const handleApprove = async (client: Client) => {
    const result = await sweetAlert.confirm(
      'Aprovar Cliente?',
      `Tem a certeza de que deseja aprovar o registo da empresa "${client.company_name}"?`
    );
    if (result.isConfirmed) {
      approveMutation.mutate(client.id);
    }
  };

  const handleReject = (client: Client) => {
    setSelectedClient(client);
    setShowRejectModal(true);
  };

  const submitRejection = () => {
    if (!selectedClient || !rejectionReason.trim()) {
      toast.warning('Por favor, indique um motivo de rejeição');
      return;
    }
    rejectMutation.mutate({ clientId: selectedClient.id, reason: rejectionReason });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Registos Pendentes</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Registos Pendentes</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <UserCheck className="w-8 h-8 text-primary-500" />
            <span>Registos de Clientes Pendentes</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Reveja os dados de conformidade (KYC) e aprove ou rejeite novas candidaturas
          </p>
        </div>
      </div>

      {pendingClients && pendingClients.length > 0 ? (
        <div className="space-y-4">
          {pendingClients.map((client) => (
            <div
              key={client.id}
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-6 transition-all hover:shadow-md"
            >
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {client.company_name}
                      </h3>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                        Aguardando Aprovação
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className="text-slate-400 font-medium">NIF / Identificação Fiscal</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200 font-mono">
                          {client.tax_id}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className="text-slate-400 font-medium">Email Corporativo</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                          {client.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className="text-slate-400 font-medium">Telefone</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200 font-mono">
                          {client.phone}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60">
                      <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className="text-slate-400 font-medium">País de Origem</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200">
                          {client.country}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60 sm:col-span-2">
                      <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className="text-slate-400 font-medium">Submetido para Análise</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200">
                          {new Date(client.created_at).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-row lg:flex-col gap-2.5 shrink-0 w-full lg:w-44">
                  <button
                    type="button"
                    onClick={() => handleApprove(client)}
                    disabled={approveMutation.isPending}
                    className="flex-1 lg:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold text-xs shadow-xs hover:shadow-md transition-all disabled:opacity-50"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    <span>Aprovar Acesso</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleReject(client)}
                    disabled={rejectMutation.isPending}
                    className="flex-1 lg:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/80 hover:bg-rose-100 dark:hover:bg-rose-900/50 font-semibold text-xs transition-all disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    <span>Rejeitar</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-8">
          <EmptyState
            title="Nenhum registo pendente"
            description="Não existem candidaturas de novos clientes aguardando aprovação no momento."
          />
        </div>
      )}

      {/* Modern Rejection Modal */}
      {showRejectModal && selectedClient && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRejectModal(false);
              setSelectedClient(null);
              setRejectionReason('');
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-800 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Rejeitar Registo
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {selectedClient.company_name}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
              Indique o motivo formal da rejeição. Esta mensagem será enviada por email ao
              candidato:
            </p>

            {rejectMutation.isError && (
              <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 p-3">
                <p className="text-xs text-rose-700 dark:text-rose-300">
                  {getErrorMessage(rejectMutation.error)}
                </p>
              </div>
            )}

            <textarea
              className="w-full h-32 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 outline-hidden transition-all resize-none"
              placeholder="Ex: Documentação fiscal (NIF) inválida ou divergente da razão social..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />

            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedClient(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitRejection}
                disabled={rejectMutation.isPending || !rejectionReason.trim()}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-semibold shadow-xs disabled:opacity-50 transition-colors"
              >
                {rejectMutation.isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>A rejeitar...</span>
                  </>
                ) : (
                  <span>Confirmar Rejeição</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
